<script>
  import { onMount, onDestroy } from "svelte";
  import { Label, Input } from "sveltestrap";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import { getFieldDataType } from "../../editor/component/data.js";
  import {
    callQueue,
    dataInfo,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    menuConfig,
    directContext,
    currMenu,
    currentElementLockGroup,
  } from "../../editor/storage.js";
  import { getCascasedVal, validateNamePattern } from "../../editor/utility.js";
  import {
    markTypes,
    markMap,
    encodingMap,
    encodingTypes,
    getDashThumbnail,
  } from "../../editor/component/mark.js";
  import { deepcopy } from "../../editor/utility.js";
  import { alertYesNo } from "../../alert.js";
  import EncodingPanel from "../component/encodingPanel.svelte";
  import LineMarkSpecial from "../component/lineMarkSpecial.svelte";
  import AreaMarkSpecial from "../component/areaMarkSpecial.svelte";
  import GeoShapeSpecial from "../component/GeoShapeSpecial.svelte";
  import BarMarkSpecial from "../component/barMarkSpecial.svelte";
  import { findLayer, unfindLayer } from "../../editor/component/layer.js";
  import ItemResponse from "../component/ItemResponse.svelte";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;
  export let openThirdMenu;
  export let closeThirdMenu;

  let layers;
  let currLayer;
  let currLayerIndex;
  let layer_names;
  let layerNumberLimit;
  function setValues() {
    layers = $currentSpec?.fronto?.layer || [];
    layers?.forEach((layer, i) => {
      layer.index = i;
    });
    layer_names = layers?.map((layer) => layer.name);
    // layers?.reverse();
    if (currLayerIndex !== undefined) {
      setCurrLayer(currLayerIndex);
    }
    if ($currentSpec?.fronto?.layout?.composition === "single") {
      if (
        $currentSpec?.fronto?.layout?.row?.length >= 2 ||
        $currentSpec?.fronto?.layout?.column?.length >= 2
      )
        layerNumberLimit = 1;
      else layerNumberLimit = Infinity;
    } else if ($currentSpec?.fronto?.layout?.composition === "repeated") {
      layerNumberLimit = 1;
    } else {
      layerNumberLimit = Infinity;
    }
  }

  let subMenu;
  function setSubMenu(s) {
    if (s == "new") {
      let new_name = "layer_" + layers.length;
      if (layer_names.includes(new_name)) new_name = new_name + "_a";
      let rule = {
        category: "manual",
        description: "Add a new layer",
        specifier: { role: "layer" },
        action: "add",
        option: {
          name: new_name,
          mark: "point",
        },
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
      setCurrLayer("new");
    }
    subMenu = s;
  }
  function cancelSubMenu() {
    subMenu = undefined;
    currLayerIndex = undefined;
    invalidDashPattern = undefined;
    currentEncoding = undefined;
    showEncodingPanel = false;
  }

  // current layer
  let currLayerPrev;
  let specialProps;
  let invalidDashPattern;
  function setCurrLayer(i) {
    if (i === "new") {
      currLayer = layers[layers.length - 1];
    } else {
      currLayer = layers.filter((layer) => layer.index == i)[0];
    }
    if (currLayer) {
      currLayerPrev = deepcopy(currLayer);
      currLayerIndex = currLayer.index;
      if (currLayerPrev.mark?.type !== undefined) {
        if (markMap[currLayerPrev.mark?.type].special !== undefined) {
          specialProps = [];
          markMap[currLayerPrev.mark?.type].special.forEach((prop) => {
            specialProps[prop.key] = getCascasedVal(
              currLayerPrev.mark,
              prop.key
            );
          });
        }
      }
    }
  }

  function editLayer(i) {
    setCurrLayer(i);
    unfindLayer();
    setSubMenu("editLayer");
  }
  function moveLayer(i, dir) {
    if (["up", "down"].includes(dir)) {
      let rule = {
        category: "manual",
        description: "Move layer " + dir + "ward",
        specifier: { role: "layer" },
        action: "swap",
        option: {
          from: { index: i },
          to: { index: i + (dir == "up" ? -1 : 1) },
        },
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  function deleteLayer(i) {
    let uid = $currentSpec.uid;
    let onYes = new CustomEvent("onYes_" + uid, {
      detail: { outcome: "yes" },
    });
    let onNo = new CustomEvent("onNo_" + uid, {
      detail: { outcome: "no" },
    });
    alertYesNo({
      title: "Delete an artboard",
      body: "Do you really want to delete Layer " + currLayer.name + "?",
      onYes: onYes,
      onNo: onNo,
    });
    function yesHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
      let rule = {
        category: "manual",
        specifier: {
          role: "layer",
          mark: currLayer.mark.type,
          id: currLayer.name,
        },
        action: "remove",
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
    function noHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
    }
    document.body.addEventListener("onYes_" + uid, yesHandler, {
      once: true,
    });
    document.body.addEventListener("onNo_" + uid, noHandler, { once: true });
  }

  let invalidLayerName;
  function checkName(event) {
    let name = event.target.value;
    return validateNamePattern(name) && !layer_names.includes(name);
  }

  let showEncodingPanel,
    currentEncoding,
    currEncType,
    currEncDataType,
    currEncSort,
    currBin,
    currBinCount,
    currAggregate,
    currEncScaleType,
    currEncFieldType,
    currScaleScheme,
    showDataColumns;
  function setEncodingMenu(enc) {
    if (encodingTypes.some((e) => e.type === enc)) {
      showEncodingPanel = true;
      openThirdMenu();
      currentEncoding = enc;
      if (currLayer?.mark?.[enc]?.value !== undefined) {
        currEncType = "static";
        showDataColumns = false;
      } else if (
        currLayer?.mark?.[enc]?.field !== undefined ||
        currLayer?.mark?.[enc]?.aggregate !== undefined
      ) {
        currEncType = "encoding";
        if (currLayer?.mark?.[enc]?.field !== undefined) {
          currEncFieldType = getFieldDataType(
            currLayer?.mark?.[enc]?.field,
            $dataInfo
          );
          currEncDataType =
            $dataInfo?.stats[currLayer?.mark?.[enc]?.field]?.type;
        } else if (currLayer?.mark?.[enc]?.aggregate === "count") {
          currEncFieldType = "number";
        }
        if (currLayer?.mark?.[enc]?.sort !== undefined) {
          if (currLayer?.mark?.[enc]?.sort?.order !== undefined) {
            currEncSort = "custom";
          } else {
            currEncSort = currLayer?.mark?.[enc]?.sort;
          }
        } else {
          currEncSort = "none";
        }
        currAggregate = currLayer?.mark?.[enc]?.aggregate || undefined;
        if (
          currLayer?.mark?.[enc]?.bin !== undefined &&
          currLayer?.mark?.[enc]?.bin !== null
        ) {
          currBin = true;
          currBinCount = currLayer?.mark?.[enc]?.bin.maxbins;
        } else {
          currBin = false;
        }
        if (currLayer?.mark?.[enc]?.scale === undefined) {
          currEncScaleType = "default";
        } else if (currLayer?.mark?.[enc]?.scale !== undefined) {
          if (
            currLayer?.mark?.[enc]?.scale.domain !== undefined &&
            currLayer?.mark?.[enc]?.scale.range !== undefined
          ) {
            currEncScaleType = "domain_range";
          } else if (currLayer?.mark?.[enc]?.scale.scheme !== undefined) {
            currEncScaleType = "scheme_only";
            currScaleScheme = currLayer?.mark?.[enc]?.scale.scheme;
          } else {
            currEncScaleType = "default";
          }
        }
        showDataColumns = true;
      } else {
        currEncType = undefined;
        showDataColumns = false;
        currEncSort = "none";
      }
    }
  }
  function cancelEncodingMenu() {
    showEncodingPanel = false;
    showDataColumns = false;
    currentEncoding = undefined;
    currEncSort = undefined;
    closeThirdMenu();
  }
  function updateLayer(event, role, layer) {
    let rule, newValue;
    if (role === "name") {
      newValue = event.target.value;
      if (checkName(event) && newValue !== undefined) {
        rule = {
          description: "Update layer name",
          specifier: {
            role: "layer",
          },
          action: "modify",
          option: { name: newValue },
        };
        if (layer.name !== undefined && layer.name !== "")
          rule.specifier.id = layer.name;
        if (layer.index !== undefined) rule.specifier.index = layer.index;
      }
    } else if (
      role === "markType" &&
      markTypes.some((d) => d.type === event.target.value)
    ) {
      newValue = event.target.value;
      rule = {
        description: "Update mark type",
        specifier: {
          role: "layer",
        },
        action: "modify",
        option: { mark: { type: newValue } },
      };
      if (layer.name !== undefined || layer.name !== "") {
        rule.specifier.id = layer.name;
      } else if (layer.index !== undefined) {
        rule.specifier.index = layer.index;
      }
      if (newValue === "area" && newValue !== layer?.mark?.clip) {
        rule.option.mark.clip = true;
      } else if (newValue !== "area") {
        rule.option.mark.clip = null;
      }
      if (!["area", "line"].includes(newValue)) {
        rule.option.mark.point = null;
      }
    } else {
      if (
        [
          "mark.point",
          "mark.point.filled",
          "mark.clip",
          "mark.endMark",
        ].includes(role)
      ) {
        newValue = event.target.checked;
      } else if (
        [
          "mark.point.size",
          "mark.cornerRadius",
          "mark.cornerRadiusEnd",
          "mark.width.band",
        ].includes(role)
      ) {
        newValue = parseFloat(event.target.value);
      } else if (["mark.feature", "mark.interpolate"].includes(role)) {
        newValue = event.target.value;
      }
      if (newValue !== undefined || newValue == null) {
        rule = {
          description: "Edit mark ",
          specifier: { role: "mark" },
          action: "modify",
          option: {},
        };
        if (layer.name !== undefined || layer.name !== "") {
          rule.specifier.id = layer.name;
        } else if (layer.index !== undefined) {
          rule.specifier.index = layer.index;
        }
        if (role === "mark.clip") {
          rule.description += "point " + (newValue ? "(set)" : "(unset)");
          rule.option = { clip: newValue };
        } else if (role === "mark.point") {
          if (newValue) {
            rule.description += "point (set)";
            rule.option = { point: true };
          } else {
            rule.description += "point (unset)";
            rule.option = { point: null };
          }
        } else if (role === "mark.feature") {
          if (newValue) {
            rule.description += "feature (set)";
            rule.option = { feature: newValue };
          } else {
            rule.description += "feature (unset)";
            rule.option = { feature: null };
          }
        } else if (role === "mark.interpolate") {
          if (newValue) {
            rule.description += "interpolate (set)";
            rule.option = { interpolate: newValue };
          } else {
            rule.description += "interpolate (unset)";
            rule.option = { interpolate: null };
          }
        } else if (role.startsWith("mark.point.")) {
          let optionKey = role.split(".")[2];
          rule.description += "point " + optionKey;
          rule.option = { point: {} };
          rule.option.point[optionKey] = newValue;
        } else if (role === "mark.endMark") {
          if (newValue) {
            rule.description += "line-end mark (set)";
            rule.option = { endMark: true };
          } else {
            rule.description += "line-end mark (unset)";
            rule.option = { endMark: null };
          }
        } else if (role === "mark.width.band") {
          if (newValue !== null) {
            rule.description += "bar width (set)";
            rule.option = { width: { band: newValue } };
          } else {
            rule.description += "bar width (unset)";
            rule.option = { width: null };
          }
        } else if (role === "mark.cornerRadius") {
          if (newValue !== null) {
            rule.description += "bar corner radius-full (set)";
            rule.option = { cornerRadius: newValue };
          } else {
            rule.description += "bar corner radius-full (unset)";
            rule.option = { cornerRadius: null };
          }
        } else if (role === "mark.cornerRadiusEnd") {
          if (newValue !== null) {
            rule.description += "bar corner radius-end (set)";
            rule.option = { cornerRadiusEnd: newValue };
          } else {
            rule.description += "bar corner radius-end (unset)";
            rule.option = { cornerRadiusEnd: null };
          }
        }
      }
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }
  function updateEncoding(event, enc, role, layer) {
    let newValue;
    if (role === "lock") {
      updateLock(event, enc, layer);
      return;
    } else if (role === "value") {
      let itype = encodingMap[enc].input_type;
      if (itype === "dash") {
        newValue = event.target.value.split(",").map((d) => parseFloat(d));
      } else if (["text", "select"].includes(itype)) {
        newValue = event.target.value;
      } else if (itype === "color") {
        newValue =
          "rgba(" +
          [event.detail.r, event.detail.g, event.detail.b, event.detail.a].join(
            ","
          ) +
          ")";
      } else if (itype === "number") {
        newValue = parseFloat(event.target.value);
      }
    } else if (role === "encoding.field") {
      newValue = event;
    } else if (role === "encoding.type") {
      newValue = event.target.value;
    } else if (role === "encoding.sort") {
      newValue = event.target.value;
      if (newValue === "custom") {
        let values = $dataInfo.stats[layer.mark?.[enc]?.field].values;
        if (values) newValue = { order: values };
      } else if (newValue === "none") {
        newValue = null;
      }
    } else if (role === "encoding.sort.order") {
      newValue = event.target.value.split(",").map((d) => d.trim());
    } else if (role === "encoding.aggregate") {
      if (event.constructor.name === "String") newValue = event;
      else newValue = event.target.value;
    } else if (role === "encoding.bin") {
      newValue = event.target.checked || null;
    } else if (role === "encoding.bin.nBins") {
      newValue = parseInt(event.target.value);
    } else if (role === "encoding.scale.type") {
      if (event.target.value === "default") newValue = null;
    } else if (role === "encoding.scale.values") {
      newValue = event;
    } else if (role === "encoding.scale.scheme") {
      newValue = event;
      currScaleScheme = event;
    }
    let rule;
    if (newValue || newValue == null) {
      rule = {
        description: `Modify ${currentEncoding} encoding `,
        specifier: { role: "mark" },
        action: "modify",
        option: {},
      };
      // specifier
      if (layer.name !== undefined || layer.name !== "")
        rule.specifier.id = layer.name;
      if (layer.index !== undefined) rule.specifier.index = layer.index;
      // option
      if (role === "value") {
        rule.description += "property value";
        rule.option[currentEncoding] = { value: newValue };
      } else if (role === "encoding.field") {
        rule.description += "field";
        rule.option[currentEncoding] = { field: newValue };
        if (newValue) {
          let newType = $dataInfo.stats[newValue].type;
          if (newType) {
            rule.option[currentEncoding].type = newType;
          }
        }
      } else if (role === "encoding.type") {
        rule.description += "type";
        rule.option[currentEncoding] = { type: newValue };
      } else if (role === "encoding.sort") {
        rule.description += "sort";
        rule.option[currentEncoding] = { sort: newValue };
      } else if (role === "encoding.sort.order") {
        rule.description += "sort order";
        rule.option[currentEncoding] = { sort: { order: newValue } };
      } else if (role === "encoding.aggregate") {
        rule.description += "aggregate";
        rule.option[currentEncoding] = { aggregate: newValue };
      } else if (role === "encoding.bin") {
        if (newValue) {
          rule.description += "bin (set)";
          rule.option[currentEncoding] = { bin: true };
        } else {
          rule.description += "bin (unset)";
          rule.option[currentEncoding] = { bin: null };
        }
      } else if (role === "encoding.bin.nBins") {
        rule.option[currentEncoding] = { bin: { nBins: newValue } };
      } else if (role === "encoding.scale.type") {
        rule.description += "scale type";
        if (newValue == null) rule.option[currentEncoding] = { scale: null };
      } else if (role === "encoding.scale.values") {
        rule.description += "scale domain and range";
        rule.option[currentEncoding] = {
          scale: {
            domain: newValue.domain_values,
            range: newValue.range_values,
          },
        };
      } else if (role === "encoding.scale.scheme") {
        rule.description += "scale scheme";
        rule.option[currentEncoding] = {
          scale: {
            domain: null,
            range: null,
            scheme: newValue,
          },
        };
      }
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  function updateLock(event, role, layer) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role && layer) {
        let rule;
        if (role === "markType") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " mark type",
            specifier: {
              role: "mark",
            },
            action: "lock",
            option: {
              markType: newValue,
            },
          };
        } else if (role === currentEncoding) {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " mark property",
            specifier: {
              role: "mark",
            },
            action: "lock",
            option: {},
          };
          rule.option[role] = newValue;
        }
        if (rule) {
          if (layer.name !== undefined || layer.name !== "")
            rule.specifier.id = layer.name;
          if (layer.index !== undefined) rule.specifier.index = layer.index;
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }

  menuConfig.subscribe((config) => {
    if (config?.layerIndex !== undefined) {
      setValues();
      editLayer(config.layerIndex);
    }
  });
  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    showEncodingPanel = false;
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["mark"]?.title || "Mark"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !subMenu}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">
          Add/edit/remove marks (layers). {#if layerNumberLimit == 1}The current
            layout format allows up to 1 layer.{/if}
        </p>
      </div>
    </div>

    {#if layerNumberLimit > 1}
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-form-row">
          <button
            class="menu-second-level"
            type="button"
            on:click={() => {
              setSubMenu("new");
            }}
            ><i class="bi bi-plus-circle me-1" />Create a new mark layer</button
          >
        </div>
      </div>
      <div class="pane-divider" />
    {/if}

    <div class="dupo-menu-pane mb-2">
      {#if layers.length > 0}
        <div class="dupo-menu-pane-row mb-2">
          <h3 class="h-sub"><i class="bi bi-layers me-1" /> Layers</h3>
        </div>
      {/if}
      {#each layers as layer}
        <div class="dupo-menu-pane-form-row">
          <div
            class="menu-second-level menu-with-buttons"
            on:mouseover={() => {
              findLayer(
                $currentSpec.uid,
                layer.name,
                layer.index,
                layerNumberLimit
              );
            }}
            on:focus={() => {
              findLayer(
                $currentSpec.uid,
                layer.name,
                layer.index,
                layerNumberLimit
              );
            }}
            on:mouseout={() => {
              unfindLayer();
            }}
            on:blur={() => {
              unfindLayer();
            }}
          >
            <div class="text-truncate" style="user-select:none;">
              <!-- convert to mark icons -->
              {#if layer.mark?.type}
                <img
                  src={`../image/mark/${markMap[layer.mark?.type].icon}`}
                  class="layer-icon me-1"
                  alt={`layer icon for mark type ${layer.mark?.type}`}
                />
              {:else}
                <i class="bi bi-question-circle me-1" />
              {/if}
              {layer.name || "untitled"} / {layer.mark?.type || "none"}
            </div>
            <div class="dupo-pane-list-button">
              <button
                type="button"
                on:click={() => {
                  editLayer(layer.index);
                }}
                ><i
                  class="bi bi-pencil-fill"
                  alt="{`Edit layer ${layer.name}`}}"
                /></button
              >
              {#if layers.length >= 2}
                <div class="two-row-buttons">
                  <button
                    type="button"
                    on:click={() => {
                      moveLayer(layer.index, "up");
                    }}
                    ><i
                      class="bi bi-arrow-up"
                      alt="{`Move up layer ${layer.name}`}}"
                    /></button
                  ><button
                    type="button"
                    on:click={() => {
                      moveLayer(layer.index, "down");
                    }}
                    ><i
                      class="bi bi-arrow-down"
                      alt="{`Move down layer ${layer.name}`}}"
                    /></button
                  >
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>
  {:else if subMenu === "new" || subMenu === "editLayer"}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        {#if subMenu === "new"}
          <p class="text-sm">Create a new mark layer.</p>
        {:else if subMenu === "editLayer"}
          <p class="text-sm">Manage a layer.</p>
        {/if}
      </div>
    </div>
    <!-- name/mark type/ecnoding&property ==> third-level -->
    <!-- name -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="layer_name">Layer name</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          type="text"
          name="layer_name"
          id="layer_name"
          class="custom-form-select"
          autocomplete="off"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            invalidLayerName = !checkName(event);
            if (!invalidLayerName) updateLayer(event, "name", currLayerPrev);
          }}
          bind:value={currLayer.name}
        />
      </div>
      {#if invalidLayerName}
        <div class="dupo-menu-pane-row warn-msg">
          <p class="text-sm">
            Invalid layer name or duplicate layer name! Should use only 0-9,
            a-z, A-Z, -, and _.
          </p>
        </div>
      {/if}
    </div>
    <!-- mark type -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="layer_name">Mark type</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <!-- mark icons -->
        <div class="mark-select">
          {#each markTypes as mark, mi}
            <div
              class={`mark-select-item c${mi % 4} ${
                mark.type === currLayer?.mark?.type ? "selected" : ""
              }`}
            >
              <img
                src={`../image/mark/${mark.icon}`}
                alt={mark.name + " icon"}
              />
              <span>{mark.name}</span>
              <input
                type="button"
                value={mark.type}
                on:click={(event) => {
                  updateLayer(event, "markType", currLayerPrev);
                }}
              />
            </div>
          {/each}
        </div>
      </div>
    </div>

    <!-- lock -->
    <div class="pane-divider" />
    <div class="dupo-menu-pane pt-2">
      <ItemResponse
        type="lock"
        tooltipMessage="Prevent suggesting responsive alternatives that make changes to the mark type. Element-Lock applies to its source or target design(s)."
        title="Lock this mark type"
        token={"mark-lock"}
        value={currLayer.mark.lock}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateLock(event, "markType", currLayer);
        }}
      />
    </div>
    <div class="pane-divider" />
    <!-- mark-special -->
    {#if currLayer.mark?.type === "line"}
      <LineMarkSpecial
        {inputFocusAction}
        {inputBlurAction}
        {updateLayer}
        {currLayer}
        {currLayerPrev}
      />
    {:else if currLayer.mark?.type === "area"}
      <AreaMarkSpecial
        {inputFocusAction}
        {inputBlurAction}
        {updateLayer}
        {currLayer}
        {currLayerPrev}
      />
    {:else if currLayer.mark?.type === "geoshape"}
      <GeoShapeSpecial
        {inputFocusAction}
        {inputBlurAction}
        {updateLayer}
        {currLayer}
        {currLayerPrev}
      />
    {:else if currLayer.mark?.type === "bar"}
      <BarMarkSpecial
        {inputFocusAction}
        {inputBlurAction}
        {updateLayer}
        {currLayer}
        {currLayerPrev}
      />
    {/if}
    <div class="pane-divider" />
    <!-- encoding -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row mb-2">
        <h3 class="h-sub">Mark property & encoding</h3>
      </div>
    </div>
    <div class="dupo-menu-pane mb-2">
      <!-- todo: list of encodings and their schema -->
      {#if currLayer.mark?.type !== undefined}
        {#if markMap[currLayer.mark.type].special !== undefined}
          {#each markMap[currLayer.mark.type].special as prop}
            <div class="dupo-menu-pane-row">
              <Label for={"prop_" + prop.key}>{prop.name}</Label>
            </div>
            <div class="dupo-menu-pane-form-row">
              <Input
                type={prop.input_type}
                data-type={prop.value_type}
                name={"prop_" + prop.key}
                id={"prop_" + prop.key}
                class="custom-form-select"
                autocomplete="off"
                on:focus={inputFocusAction}
                on:blur={inputBlurAction}
                on:change={(event) => {
                  updateLayer(event, "mark." + prop.key, currLayerPrev);
                }}
                bind:value={specialProps[prop.key]}
              />
            </div>
          {/each}
        {/if}
        {#each markMap[currLayer.mark.type].encodings as enc}
          <div class="dupo-menu-pane-form-row">
            <div class="menu-second-level menu-with-buttons">
              <div class="text-truncate" style="user-select:none;">
                <img
                  src={`../image/encoding/${encodingMap[enc].icon}`}
                  class="enc-icon"
                  alt={encodingMap[enc].name + " icon"}
                />{encodingMap[enc].name}<span class="curr ms-2"
                  >{#if currLayer.mark[enc]?.field}<i
                      class="bi bi-caret-right-fill"
                    />{:else if currLayer.mark[enc]?.aggregate}<i
                      class="bi bi-caret-right"
                    />{/if}{currLayer.mark[enc]?.field ||
                    currLayer.mark[enc]?.aggregate ||
                    ""}
                  {#if currLayer.mark[enc]?.value !== undefined}
                    {#each encodingMap[enc].display_value as dv}
                      {#if dv === "color"}
                        <i class="bi bi-caret-right-fill" />
                        <i
                          class="bi bi-square-fill ms-1"
                          style={`color: ${currLayer.mark[enc].value};`}
                        />
                      {:else if dv === "strokeDash"}
                        <i class="bi bi-caret-right-fill" />
                        <div
                          class="stroke-thumbnail ms-1"
                          style={getDashThumbnail(
                            currLayer.mark[enc].value
                              .split(",")
                              .map((d) => parseFloat(d))
                          )}
                        />
                      {:else if dv === "strokeWidth"}
                        <i class="bi bi-caret-right-fill" />
                        <div
                          class="stroke-thumbnail ms-1"
                          style={`height: ${currLayer.mark[enc].value}px;`}
                        />
                      {:else if dv === "opacity"}
                        <i class="bi bi-caret-right-fill" />
                        <i
                          class="bi bi-square-fill ms-1"
                          style={`color: black; opacity: ${currLayer.mark[enc].value};`}
                        />
                      {:else}
                        {currLayer.mark[enc].value}
                      {/if}
                    {/each}
                  {/if}</span
                >
              </div>
              <div class="dupo-pane-list-button">
                <button
                  type="button"
                  value={encodingMap[enc].type}
                  on:click={() => {
                    setEncodingMenu(enc);
                  }}><i class="bi bi-pencil-fill" /></button
                >
              </div>
            </div>
          </div>
        {/each}
      {/if}
    </div>
    <div class="pane-divider" />
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            deleteLayer(currLayerIndex);
            if ($directContext) currMenu.set("selection");
            else {
              unfindLayer();
              cancelSubMenu();
            }
          }}><i class="bi bi-eraser-fill me-1" />Delete this layer</button
        >
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else {
              unfindLayer();
              cancelSubMenu();
              closeThirdMenu();
            }
          }}><i class="bi bi-arrow-left me-1" />Go back</button
        >
      </div>
    </div>
  {/if}
</section>

{#if showEncodingPanel}
  <section class="third-level-menu">
    <EncodingPanel
      {currentEncoding}
      {cancelEncodingMenu}
      {currEncType}
      {inputFocusAction}
      {inputBlurAction}
      {invalidDashPattern}
      {currLayerPrev}
      {updateEncoding}
      {currLayer}
      {currLayerIndex}
      {currEncDataType}
      {currEncSort}
      {currBin}
      {currBinCount}
      {currAggregate}
      {currScaleScheme}
      {currEncFieldType}
      {currEncScaleType}
      {showDataColumns}
    />
  </section>
{/if}

<style>
  .layer-icon {
    width: 1rem;
    height: 1rem;
  }
  .mark-select {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .mark-select-item {
    display: block;
    flex: 0 0 23%;
    padding: 0;
    border-radius: var(--default-radius);
    position: relative;
  }
  .mark-select-item img {
    display: block;
    width: 24px;
    height: 24px;
    margin: 0.6rem auto 0.3rem auto;
    opacity: 0.5;
  }
  .mark-select-item span {
    display: block;
    width: 100%;
    font-size: 0.6rem;
    color: #454545;
    text-align: center;
    margin: 0.3rem auto 0.4rem auto;
  }
  .mark-select-item:hover {
    background-color: #ffffff;
  }
  .mark-select-item:hover img {
    opacity: 0.8;
  }
  .mark-select-item:hover span {
    font-weight: 500;
  }
  .mark-select-item.selected {
    background-color: #ffffff;
    border: 1px solid #dddddd;
  }
  .mark-select-item.selected img {
    opacity: 1;
  }
  .mark-select-item.selected span {
    font-weight: 600;
  }
  .mark-select-item input[type="button"] {
    position: absolute;
    left: 0;
    top: 0;
    width: 60px;
    height: 60px;
    display: block;
    background: transparent;
    border: 0;
    font-size: 0;
    user-select: none;
  }
  :global(.enc-icon) {
    width: 0.78rem;
    height: 0.78rem;
    margin-right: 0.25rem;
    vertical-align: middle;
  }
  .color-name {
    background-color: rgba(0, 0, 0, 0.085);
    min-width: 4rem;
    font-family: monospace !important;
  }
  .stroke-thumbnail {
    display: inline-block;
    border: 0;
    width: 20px;
    transform: translateY(0.5rem);
    background: black;
    vertical-align: text-top;
  }
  span.curr {
    color: #797979;
  }
</style>
